import {
  Box,
  Button,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useContext, useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";
import { AppContext } from "../../../../contexts/AppContext";
import { ResultsService } from "../../../../services/results/ResultsService";


interface ServerBreakdown {
  claimed: number;
  unclaimed: number;
}

interface GiftCardStats {
  claimed: number;
  limit: number;
  remaining: number;
  exhausted: boolean;
  byServer: {
    ALPHA: ServerBreakdown;
    BETA: ServerBreakdown;
    GAMMA: ServerBreakdown;
    OMEGA: ServerBreakdown;
  };
  byStore: { WALMART: number; IGA: number; MAXI: number };
  unclaimedTotal: number;
}

interface ClaimedRow {
  firstname?: string;
  name?: string;
  username: string;
  store: string;
  server: string;
}

interface UnclaimedRow {
  firstname?: string;
  name?: string;
  username: string;
  server: string;
}


const STORES = ["WALMART", "IGA", "MAXI"] as const;
type StoreChoice = typeof STORES[number];

const GiftCardTracker = () => {
  const { jwtToken } = useContext(AppContext);
  const [stats, setStats] = useState<GiftCardStats | null>(null);
  const [claimedList, setClaimedList] = useState<ClaimedRow[]>([]);
  const [unclaimedList, setUnclaimedList] = useState<UnclaimedRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [updatedAt, setUpdatedAt] = useState<string>("");
  // Per-row in-progress assign state — keyed by username
  const [assignChoice, setAssignChoice] = useState<Record<string, StoreChoice>>({});
  const [assigning, setAssigning] = useState<Record<string, boolean>>({});

  const assignCard = async (username: string) => {
    if (!jwtToken) return;
    const store = assignChoice[username];
    if (!store) {
      toast("Sélectionnez un détaillant d'abord.");
      return;
    }
    setAssigning((s) => ({ ...s, [username]: true }));
    try {
      await ResultsService.assignReward(jwtToken, username, store);
      toast(`Carte ${store} attribuée à ${username}`);
      // Refresh lists + stats
      await fetchStats();
    } catch (e: any) {
      toast(`Échec : ${e?.response?.data?.msg ?? e?.message ?? "erreur"}`);
    } finally {
      setAssigning((s) => ({ ...s, [username]: false }));
    }
  };

  const fetchStats = useCallback(async () => {
    if (!jwtToken) return;
    setLoading(true);
    setError("");
    try {
      const [statusRes, listRes] = await Promise.all([
        ResultsService.getStatus(jwtToken),
        ResultsService.getRewardList(jwtToken).catch(() => ({ data: { claimed: [], unclaimed: [] } })),
      ]);
      setStats(statusRes.data?.stats?.giftCards ?? null);
      setClaimedList(listRes.data?.claimed ?? []);
      setUnclaimedList(listRes.data?.unclaimed ?? []);
      setUpdatedAt(new Date().toLocaleTimeString());
    } catch (e: any) {
      setError(e?.message ?? "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [jwtToken]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  if (!stats && loading) {
    return <Typography>Chargement…</Typography>;
  }

  if (error) {
    return (
      <Stack spacing={2}>
        <Typography color="error">Erreur : {error}</Typography>
        <Button onClick={fetchStats} variant="outlined">Réessayer</Button>
      </Stack>
    );
  }

  if (!stats) return null;

  const usedPct = stats.limit > 0 ? Math.min(100, Math.round((stats.claimed / stats.limit) * 100)) : 0;

  const servers: Array<keyof typeof stats.byServer> = ["ALPHA", "BETA", "GAMMA", "OMEGA"];
  const totals = {
    claimed: stats.claimed,
    unclaimed: stats.unclaimedTotal,
  };

  return (
    <Stack spacing={3}>
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="h6">Suivi des cartes cadeau</Typography>
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchStats}
            disabled={loading}
            size="small"
            variant="outlined"
          >
            Rafraîchir
          </Button>
        </Stack>

        <Stack direction="row" spacing={4} sx={{ mb: 2 }}>
          <Stat label="Distribuées" value={stats.claimed} />
          <Stat label="Limite" value={stats.limit} />
          <Stat
            label="Restantes"
            value={stats.remaining}
            color={stats.remaining === 0 ? "error.main" : "success.main"}
          />
          <Stat label="Non réclamées" value={stats.unclaimedTotal} tooltip="Joueurs ayant complété sans choisir de carte" />
          <Stat
            label="Statut"
            value={stats.exhausted ? "ÉPUISÉ" : "Disponible"}
            color={stats.exhausted ? "error.main" : "success.main"}
          />
        </Stack>

        <Box sx={{ mb: 0.5 }}>
          <LinearProgress
            variant="determinate"
            value={usedPct}
            color={stats.exhausted ? "error" : usedPct >= 90 ? "warning" : "primary"}
            sx={{ height: 10, borderRadius: 5 }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary">
          {usedPct}% utilisé ({stats.claimed} / {stats.limit})
          {updatedAt && ` — mis à jour à ${updatedAt}`}
        </Typography>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>Par serveur</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><b>Serveur</b></TableCell>
                <TableCell align="right"><b>Cartes réclamées</b></TableCell>
                <TableCell align="right"><b>Non réclamées</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {servers.map((s) => (
                <TableRow key={s}>
                  <TableCell>{s}</TableCell>
                  <TableCell align="right">{stats.byServer[s].claimed}</TableCell>
                  <TableCell align="right">{stats.byServer[s].unclaimed}</TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ "& td": { fontWeight: "bold", borderTop: "2px solid", borderTopColor: "divider" } }}>
                <TableCell>Total</TableCell>
                <TableCell align="right">{totals.claimed}</TableCell>
                <TableCell align="right">{totals.unclaimed}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>Par détaillant</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><b>Détaillant</b></TableCell>
                <TableCell align="right"><b>Cartes</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow><TableCell>Walmart</TableCell><TableCell align="right">{stats.byStore.WALMART}</TableCell></TableRow>
              <TableRow><TableCell>IGA</TableCell><TableCell align="right">{stats.byStore.IGA}</TableCell></TableRow>
              <TableRow><TableCell>Maxi</TableCell><TableCell align="right">{stats.byStore.MAXI}</TableCell></TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
          Cartes attribuées ({claimedList.length})
        </Typography>
        <TableContainer sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell><b>Prénom</b></TableCell>
                <TableCell><b>Nom</b></TableCell>
                <TableCell><b>Courriel</b></TableCell>
                <TableCell><b>Détaillant choisi</b></TableCell>
                <TableCell><b>Serveur</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {claimedList.map((u) => (
                <TableRow key={u.username}>
                  <TableCell>{u.firstname ?? "—"}</TableCell>
                  <TableCell>{u.name ?? "—"}</TableCell>
                  <TableCell>{u.username}</TableCell>
                  <TableCell>{u.store}</TableCell>
                  <TableCell>{u.server}</TableCell>
                </TableRow>
              ))}
              {claimedList.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ color: "text.secondary" }}>
                    Aucune carte attribuée pour le moment.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: "bold" }}>
          Non réclamées — partie complétée mais aucune carte sélectionnée ({unclaimedList.length})
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          Ces personnes doivent refaire la simulation et choisir une carte à l'écran de récompense pour réclamer.
        </Typography>
        <TableContainer sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell><b>Prénom</b></TableCell>
                <TableCell><b>Nom</b></TableCell>
                <TableCell><b>Courriel</b></TableCell>
                <TableCell><b>Serveur</b></TableCell>
                <TableCell><b>Attribuer manuellement</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {unclaimedList.map((u) => (
                <TableRow key={u.username}>
                  <TableCell>{u.firstname ?? "—"}</TableCell>
                  <TableCell>{u.name ?? "—"}</TableCell>
                  <TableCell>{u.username}</TableCell>
                  <TableCell>{u.server}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <FormControl size="small" sx={{ minWidth: 130 }}>
                        <InputLabel>Détaillant</InputLabel>
                        <Select
                          value={assignChoice[u.username] ?? ""}
                          label="Détaillant"
                          onChange={(e) => setAssignChoice((s) => ({ ...s, [u.username]: e.target.value as StoreChoice }))}
                        >
                          {STORES.map((s) => (
                            <MenuItem key={s} value={s}>{s}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={!assignChoice[u.username] || !!assigning[u.username]}
                        onClick={() => assignCard(u.username)}
                      >
                        {assigning[u.username] ? "..." : "Attribuer"}
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {unclaimedList.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ color: "text.secondary" }}>
                    Aucune partie complétée sans carte sélectionnée.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Stack>
  );
};


interface StatProps {
  label: string;
  value: string | number;
  color?: string;
  tooltip?: string;
}

const Stat = ({ label, value, color }: StatProps) => (
  <Box sx={{ minWidth: 110 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="h5" sx={{ fontWeight: "bold", color: color ?? "text.primary" }}>
      {value}
    </Typography>
  </Box>
);


export default GiftCardTracker;
